import { useCallback, useEffect, useMemo, useState } from 'react';
import { hostname } from '../providers/api';
import { useTokenOps } from '../components/TokenContext';
import { PERMISSIONS } from '../auth/permissions';

export const legacyAuthorization = (isLegacyAdmin) => ({
  loading: false,
  roles: isLegacyAdmin ? ['ADMIN'] : [],
  permissions: isLegacyAdmin ? Object.values(PERMISSIONS) : [],
  scope: isLegacyAdmin ? 'GLOBAL' : 'ACCOUNT',
});

const emptyAuthorization = () => ({
  loading: false,
  roles: [],
  permissions: [],
  scope: 'ACCOUNT',
});

export const authorizationFailureState = (status, isLegacyAdmin) =>
  status === 404 ? legacyAuthorization(isLegacyAdmin) : emptyAuthorization();

export default function useAuthorization() {
  const tokenOps = useTokenOps();
  const token = tokenOps.account && tokenOps.account.accessToken;
  const isLegacyAdmin = Boolean(
    tokenOps.root?.data?.roles?.includes('ADMIN'),
  );
  const [state, setState] = useState({ loading: true, roles: [], permissions: [], scope: 'ACCOUNT' });
  useEffect(() => {
    let active = true;
    if (!token) { setState({ loading: false, roles: [], permissions: [], scope: 'ACCOUNT' }); return undefined; }
    setState(current => ({ ...current, loading: true }));
    fetch(`${hostname}/authorization/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then((response) => {
        if (response.ok) return response.json();
        const error = new Error(`Authorization failed: ${response.status}`);
        error.status = response.status;
        return Promise.reject(error);
      })
      .then(value => active && setState({ loading: false, ...value }))
      .catch((error) =>
        active &&
        setState(authorizationFailureState(error.status, isLegacyAdmin)),
      );
    return () => { active = false; };
  }, [token, isLegacyAdmin]);
  const can = useCallback(code => state.permissions.includes(code), [state.permissions]);
  return useMemo(() => ({
    ...state,
    can,
    isGlobalAdmin: state.scope === 'GLOBAL' && state.roles.includes('ADMIN'),
    // Dormant RBAC reactivation: expose the prior SUPER_ADMIN-derived value here.
    isSuperAdmin: false,
  }), [state, can]);
}
